<template>
  <AModal
    :model-value="visible"
    size="md"
    @update:model-value="(v) => !v && closeModal()"
  >
    <template #title>
      <div class="edt-modal__title">
        <i class="material-icons-round">person_add</i>
        <span>새 편집자 추가</span>
      </div>
    </template>

    <div class="edt-modal__form">
      <!-- 이름 -->
      <div class="p-field">
        <label class="p-field__label">
          <i class="material-icons-round p-field__label-icon">person</i>
          이름 <span class="p-field__req">*</span>
        </label>
        <input
          v-model="formData.userName"
          type="text"
          class="p-input"
          :maxlength="inputMax.name"
          placeholder="이름을 입력하세요"
        />
      </div>

      <!-- 이메일 + 전화 -->
      <div class="p-form-grid p-form-grid--2">
        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">email</i>
            이메일 <span class="p-field__req">*</span>
          </label>
          <input
            v-model="formData.userEmail"
            type="email"
            class="p-input"
            :maxlength="inputMax.email"
            placeholder="이메일을 입력하세요"
          />
        </div>
        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">phone</i>
            전화번호
          </label>
          <input
            v-model="formData.phone"
            type="tel"
            class="p-input"
            :maxlength="inputMax.phone"
            placeholder="전화번호를 입력하세요"
          />
        </div>
      </div>

      <!-- 메모 -->
      <div class="p-field">
        <label class="p-field__label">
          <i class="material-icons-round p-field__label-icon">note</i>
          메모
        </label>
        <textarea
          v-model="formData.memo"
          class="p-input p-input--area"
          :maxlength="inputMax.memo"
          placeholder="메모를 입력하세요"
          rows="3"
        />
        <p class="p-field__hint">{{ formData.memo.length }}/{{ inputMax.memo }}자</p>
      </div>

      <div class="edt-modal__divider"><span>비밀번호 설정</span></div>

      <!-- 비밀번호 + 확인 -->
      <div class="p-form-grid p-form-grid--2">
        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">lock</i>
            비밀번호 <span class="p-field__req">*</span>
          </label>
          <div class="edt-modal__pw-wrap">
            <input
              v-model="formData.password"
              :type="showPassword ? 'text' : 'password'"
              class="p-input"
              placeholder="비밀번호를 입력하세요"
            />
            <button type="button" class="edt-modal__pw-toggle" @click="showPassword = !showPassword">
              <i class="material-icons-round">{{ showPassword ? 'visibility_off' : 'visibility' }}</i>
            </button>
          </div>
        </div>
        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">lock_outline</i>
            비밀번호 확인 <span class="p-field__req">*</span>
          </label>
          <div class="edt-modal__pw-wrap">
            <input
              v-model="formData.password_confirm"
              :type="showPasswordConfirm ? 'text' : 'password'"
              class="p-input"
              :class="{ 'is-invalid': formData.password_confirm && !passwordMatch }"
              placeholder="비밀번호를 다시 입력하세요"
            />
            <button type="button" class="edt-modal__pw-toggle" @click="showPasswordConfirm = !showPasswordConfirm">
              <i class="material-icons-round">{{ showPasswordConfirm ? 'visibility_off' : 'visibility' }}</i>
            </button>
          </div>
          <p v-if="formData.password_confirm && !passwordMatch" class="p-field__error">
            비밀번호가 일치하지 않습니다
          </p>
          <p v-if="formData.password_confirm && passwordMatch" class="edt-modal__ok-hint">
            <i class="material-icons-round">check_circle</i>
            비밀번호가 일치합니다
          </p>
        </div>
      </div>
    </div>

    <template #foot>
      <ABtn kind="ghost" @click="closeModal">취소</ABtn>
      <ABtn
        kind="yellow"
        :icon="isSubmitting ? '' : 'person_add'"
        :loading="isSubmitting"
        :disabled="!isFormValid || isSubmitting"
        @click="submitForm"
      >{{ isSubmitting ? '처리중' : '편집자 등록' }}</ABtn>
    </template>
  </AModal>
</template>

<script>
import AModal from "@/components/Pineapple/AModal.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import { EDITOR_INPUT_MAX } from "@/utils/editorAccount";

export default {
  name: "EditorCreateModal",
  components: { AModal, ABtn },
  props: {
    visible: { type: Boolean, default: false },
  },
  emits: ["close", "success"],
  data() {
    return {
      inputMax: EDITOR_INPUT_MAX,
      isSubmitting: false,
      showPassword: false,
      showPasswordConfirm: false,
      formData: {
        userName: "",
        userEmail: "",
        phone: "",
        memo: "",
        password: "",
        password_confirm: "",
      },
    };
  },
  computed: {
    passwordMatch() {
      return this.formData.password === this.formData.password_confirm;
    },
    isFormValid() {
      return (
        this.formData.userName.trim() !== "" &&
        this.formData.userEmail.trim() !== "" &&
        this.formData.password.trim() !== "" &&
        this.passwordMatch
      );
    },
  },
  watch: {
    visible(newVal) {
      if (newVal) this.resetForm();
    },
  },
  methods: {
    closeModal() {
      this.$emit("close");
    },
    resetForm() {
      this.formData = {
        userName: "",
        userEmail: "",
        phone: "",
        memo: "",
        password: "",
        password_confirm: "",
      };
      this.isSubmitting = false;
      this.showPassword = false;
      this.showPasswordConfirm = false;
    },
    async submitForm() {
      if (!this.isFormValid) {
        alert("필수 항목을 모두 입력해주세요.");
        return;
      }
      if (!this.passwordMatch) {
        alert("비밀번호가 일치하지 않습니다.");
        return;
      }
      this.isSubmitting = true;
      try {
        const res = await this.$axios({
          method: "post",
          url: "/auth/editor/register",
          data: {
            userName: this.formData.userName,
            userEmail: this.formData.userEmail,
            password: this.formData.password,
            phone: this.formData.phone,
            memo: this.formData.memo,
          },
          loadingBar: true,
        });
        if (res.status === 200 || res.status === 201) {
          alert("편집자가 성공적으로 등록되었습니다.");
          this.$emit("success");
          this.closeModal();
        }
      } catch (error) {
        console.error("편집자 등록 실패:", error);
        const errorMessage =
          error.response?.data?.detail ||
          error.response?.data?.message ||
          "편집자 등록에 실패했습니다.";
        alert(errorMessage);
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.edt-modal {
  &__title {
    display: inline-flex;
    align-items: center;
    gap: 8px;

    > i { font-size: 20px; color: $p-point-deep; }
    > span { font-size: 15px; font-weight: 800; color: $p-ink; }
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__divider {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 6px 0;

    &::before,
    &::after {
      content: '';
      flex: 1;
      height: 1px;
      background: $p-line;
    }

    > span {
      font-size: 10px;
      font-weight: 800;
      color: $p-ink-soft;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      white-space: nowrap;
    }
  }

  &__pw-wrap {
    position: relative;

    > .p-input { padding-right: 40px; }
  }

  &__pw-toggle {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    width: 28px;
    height: 28px;
    border: 0;
    background: transparent;
    color: $p-ink-soft;
    border-radius: $p-radius-sm;
    display: grid;
    place-items: center;
    cursor: pointer;
    transition: background $p-dur $p-ease, color $p-dur $p-ease;

    > i { font-size: 16px; }

    &:hover {
      background: $p-point-soft;
      color: $p-point-deep;
    }
  }

  &__ok-hint {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin: 4px 0 0;
    font-size: 11px;
    color: $p-ok;
    font-weight: 700;

    > i { font-size: 14px !important; }
  }
}
</style>
